import React, { useState, useEffect } from 'react';
import { 
  X, 
  MapPin, 
  Phone, 
  Clock, 
  Star, 
  Navigation, 
  Gift, 
  History, 
  AlertCircle,
  Plus
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import OfferCard from '../common/OfferCard';
import SearchFilter from '../common/SearchFilter';
import Pagination from '../common/Pagination';
import toast from 'react-hot-toast';
import { 
  ShopDetails as getShopDetailsAPI, 
  AllCustomerShopping,
  fetchDataset 
} from '../../services/api';

const ShopDetails = ({ shop, onClose }) => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('offers');
  const [offers, setOffers] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimAmount, setClaimAmount] = useState('');
  const [claimDescription, setClaimDescription] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('');

  useEffect(() => {
    fetchShopData();
  }, [shop.id]);

  const fetchShopData = async () => {
    setLoading(true);
    try {
      // Fetch real shop details
      const shopDetailsResponse = await getShopDetailsAPI({ shopId: shop.id });
      
      // Fetch customer shopping data for this shop
      const customerShoppingResponse = await AllCustomerShopping({ 
        shopId: shop.id,
        customerId: currentUser?.uid 
      });
      
      // Fetch offers/transactions dataset
      const datasetResponse = await fetchDataset({ 
        shopId: shop.id,
        customerId: currentUser?.uid 
      });

      // Process offers from API response
      const offersData = datasetResponse.data?.offers || [];
      setOffers(offersData);

      // Process purchase history from API response
      const historyData = customerShoppingResponse.data?.transactions || [];
      setPurchaseHistory(historyData);

      // Calculate user points from API data
      const totalPoints = customerShoppingResponse.data?.totalPoints || 0;
      setUserPoints(totalPoints);
    } catch (error) {
      console.error('Error fetching shop data:', error);
      toast.error('Failed to load shop details');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimOffer = async (offerId) => {
    try {
      // Real API call to claim offer
      const response = await AllCustomerShopping({
        action: 'claimOffer',
        offerId: offerId,
        customerId: currentUser?.uid,
        shopId: shop.id
      });
      
      if (response.data?.success) {
        // Update local state
        setOffers(prev => prev.map(offer => 
          offer.id === offerId 
            ? { ...offer, claimed: true }
            : offer
        ));
        toast.success('Offer claimed successfully!');
      } else {
        toast.error('Failed to claim offer');
      }
    } catch (error) {
      console.error('Error claiming offer:', error);
      toast.error('Failed to claim offer');
    }
  };

  const handleRedeemOffer = async (offerId) => {
    try {
      // Real API call to redeem offer
      const response = await AllCustomerShopping({
        action: 'redeemOffer',
        offerId: offerId,
        customerId: currentUser?.uid,
        shopId: shop.id
      });
      
      if (response.data?.success) {
        // Update local state
        setOffers(prev => prev.map(offer => 
          offer.id === offerId 
            ? { ...offer, redeemed: true }
            : offer
        ));
        toast.success('Offer redeemed successfully!');
      } else {
        toast.error('Failed to redeem offer');
      }
    } catch (error) {
      console.error('Error redeeming offer:', error);
      toast.error('Failed to redeem offer');
    }
  };

  const handleClaimPoints = async () => {
    if (!claimAmount || !claimDescription) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      // Real API call to claim points
      const response = await AllCustomerShopping({
        action: 'claimPoints',
        amount: claimAmount,
        description: claimDescription,
        customerId: currentUser?.uid,
        shopId: shop.id
      });
      
      if (response.data?.success) {
        toast.success('Points claim submitted for review');
        setShowClaimModal(false);
        setClaimAmount('');
        setClaimDescription('');
        // Refresh shop data to get updated points
        fetchShopData();
      } else {
        toast.error('Failed to submit points claim');
      }
    } catch (error) {
      console.error('Error claiming points:', error);
      toast.error('Failed to submit points claim');
    }
  };

  const openGoogleMaps = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${shop.location.latitude},${shop.location.longitude}`;
    window.open(url, '_blank');
  };

  const filterOptions = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'expired', label: 'Expired' }
      ]
    },
    {
      key: 'claimed',
      label: 'Claimed Status',
      type: 'select',
      options: [
        { value: 'claimed', label: 'Claimed' },
        { value: 'unclaimed', label: 'Not Claimed' }
      ]
    }
  ];

  const sortOptions = [
    { value: 'expiryDate', label: 'Expiry Date' },
    { value: 'discountValue', label: 'Discount Value' },
    { value: 'createdAt', label: 'Date Created' }
  ];

  const purchaseFilterOptions = [
    {
      key: 'paymentMethod',
      label: 'Payment Method',
      type: 'select',
      options: [
        { value: 'Card', label: 'Card' },
        { value: 'Cash', label: 'Cash' },
        { value: 'UPI', label: 'UPI' }
      ]
    },
    {
      key: 'amount',
      label: 'Amount Range',
      type: 'range'
    }
  ];

  const purchaseSortOptions = [
    { value: 'date', label: 'Date' },
    { value: 'amount', label: 'Amount' },
    { value: 'pointsEarned', label: 'Points Earned' }
  ];

  // Apply filters and pagination based on active tab
  const getFilteredData = () => {
    let data = activeTab === 'offers' ? offers : purchaseHistory;
    
    if (searchTerm) {
      data = data.filter(item => {
        if (activeTab === 'offers') {
          return item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 item.description.toLowerCase().includes(searchTerm.toLowerCase());
        } else {
          return item.items.some(i => i.toLowerCase().includes(searchTerm.toLowerCase()));
        }
      });
    }

    // Apply filters (simplified for demo)
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        data = data.filter(item => {
          // Add filter logic based on key
          return true; // Simplified
        });
      }
    });

    return data;
  };

  const filteredData = getFilteredData();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content max-w-4xl w-full max-h-screen overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`${theme.gradient} p-6 text-white relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-start space-x-4">
            <img
              src={shop.image}
              alt={shop.name}
              className="w-20 h-20 rounded-lg object-cover border-2 border-white border-opacity-20"
            />
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{shop.name}</h2>
              <div className="flex items-center space-x-4 text-sm opacity-90">
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 fill-current" />
                  <span>{shop.rating}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span>{shop.distance} km away</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span className={shop.isOpen ? 'text-green-200' : 'text-red-200'}>
                    {shop.isOpen ? 'Open' : 'Closed'}
                  </span>
                </div>
              </div>
              <p className="text-sm opacity-80 mt-2">{shop.address}</p>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center space-x-4 text-sm">
              <span>Points earned here: {userPoints}</span>
              <span>{shop.pointsPerRupee}x points per ₹</span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={openGoogleMaps}
                className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
              >
                <Navigation className="w-4 h-4" />
                <span>Navigate</span>
              </button>
              <a
                href={`tel:${shop.phone}`}
                className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>Call</span>
              </a>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('offers')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'offers'
                  ? `${theme.border.primary} ${theme.text.primary}`
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Gift className="w-4 h-4" />
                <span>Offers ({offers.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'history'
                  ? `${theme.border.primary} ${theme.text.primary}`
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <History className="w-4 h-4" />
                <span>Purchase History ({purchaseHistory.length})</span>
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Claim Points Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowClaimModal(true)}
              className={`flex items-center space-x-2 ${theme.button} px-4 py-2 rounded-lg transition-colors`}
            >
              <Plus className="w-4 h-4" />
              <span>Claim Missing Points</span>
            </button>
          </div>

          {/* Search and Filter */}
          <SearchFilter
            onSearch={setSearchTerm}
            onFilter={setFilters}
            onSort={setSortBy}
            searchPlaceholder={
              activeTab === 'offers' 
                ? "Search offers..." 
                : "Search purchase history..."
            }
            filters={activeTab === 'offers' ? filterOptions : purchaseFilterOptions}
            sortOptions={activeTab === 'offers' ? sortOptions : purchaseSortOptions}
          />

          {/* Content based on active tab */}
          {activeTab === 'offers' ? (
            <div>
              {paginatedData.length === 0 ? (
                <div className="text-center py-12">
                  <Gift className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No offers available</h3>
                  <p className="text-gray-600">Check back later for new offers from this shop.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {paginatedData.map((offer) => (
                    <OfferCard
                      key={offer.id}
                      offer={offer}
                      onClaim={handleClaimOffer}
                      onRedeem={handleRedeemOffer}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              {paginatedData.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No purchase history</h3>
                  <p className="text-gray-600">Your purchases at this shop will appear here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {paginatedData.map((purchase) => (
                    <div key={purchase.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">₹{purchase.amount}</span>
                        <span className="text-sm text-gray-500">
                          {new Date(purchase.date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">
                          Items: {purchase.items.join(', ')}
                        </span>
                        <span className="text-sm font-medium text-green-600">
                          +{purchase.pointsEarned} points
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Payment: {purchase.paymentMethod}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredData.length}
            />
          )}
        </div>
      </div>

      {/* Claim Points Modal */}
      {showClaimModal && (
        <div className="modal-overlay" onClick={() => setShowClaimModal(false)}>
          <div 
            className="modal-content max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Claim Missing Points</h3>
                <button
                  onClick={() => setShowClaimModal(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Purchase Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={claimAmount}
                    onChange={(e) => setClaimAmount(e.target.value)}
                    className="input-field"
                    placeholder="Enter purchase amount"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={claimDescription}
                    onChange={(e) => setClaimDescription(e.target.value)}
                    className="input-field h-24 resize-none"
                    placeholder="Describe your purchase and why points weren't credited"
                  />
                </div>
                
                <div className="flex items-start space-x-2 text-sm text-gray-600">
                  <AlertCircle className="w-4 h-4 mt-0.5 text-amber-500" />
                  <p>
                    Your claim will be reviewed by the shop owner. Please provide accurate information.
                  </p>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowClaimModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClaimPoints}
                    className={`flex-1 ${theme.button} py-2 px-4 rounded-lg transition-colors`}
                  >
                    Submit Claim
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopDetails;
